import type {
  VolunteerLocationInfo,
  VolunteerTrackingParams,
} from './mappings-volunteer.ts';
import {
  buildVolunteerEventFields,
  buildVolunteerGuestContent,
  buildVolunteerSelectContent,
  buildVolunteerViewContent,
  mapLocationToDatalayer,
} from './mappings-volunteer.ts';
import { trackEvent } from './transport.ts';

/* eslint-disable @typescript-eslint/naming-convention */

const events = {
  contentListView: 'view_content_list',
  contentSelect: 'select_content',
  contentView: 'view_content',
  registrationComplete: 'volunteer_registration_complete',
  registrationGuestAdd: 'volunteer_registration_add_guest',
  registrationStep: 'volunteer_registration_step',
  search: 'volunteer_search',
};

export const trackVolunteerViewContent = (params: {
  location: VolunteerLocationInfo;
  /** The tour's events; when length > 1 the payload uses the tour shape. */
  tourLocations?: VolunteerLocationInfo[];
  /** Overrides content_title (e.g. the tour name on a tour page). */
  title?: string | null;
}) => {
  trackEvent({
    event: events.contentView,
    ...buildVolunteerViewContent({
      location: params.location,
      tourLocations: params.tourLocations,
      title: params.title,
    }),
    content_url: globalThis.location?.href || '',
  });
};

export const trackVolunteerViewContentList = (params: {
  locations: VolunteerLocationInfo[];
  contentListId?: string;
  contentListName?: string;
}) => {
  trackEvent({
    event: events.contentListView,
    content_list_id: params.contentListId || 'volunteer-upcoming-events',
    content_list_name: params.contentListName || 'Volunteer Upcoming Events',
    contents: params.locations.map(mapLocationToDatalayer),
  });
};

export const trackVolunteerSelectContent = (params: {
  location: VolunteerLocationInfo;
  list?: { slug?: string; tourName?: string | null };
  isTour?: boolean;
  /** Absolute URL of the destination page; */
  contentUrl?: string;
}) => {
  trackEvent({
    event: events.contentSelect,
    ...buildVolunteerSelectContent({
      location: params.location,
      list: params.list,
      isTour: params.isTour,
    }),
    content_url: params.contentUrl || globalThis.location?.href || '',
  });
};

export const trackVolunteerSearch = (params: {
  contentListName: string;
  contentListId: string;
  eventPostalCode: string;
  sortBy: string;
  locations: VolunteerLocationInfo[];
}) => {
  trackEvent({
    event: events.search,
    content_list_id: params.contentListId,
    content_list_name: params.contentListName,
    event_postal_code: params.eventPostalCode,
    sort_by: params.sortBy,
    contents: params.locations.map(mapLocationToDatalayer),
  });
};

const pushVolunteerEvent = (
  eventName: string,
  params: VolunteerTrackingParams,
) => {
  trackEvent({
    event: eventName,
    ...mapLocationToDatalayer(params.location),
    content_url: globalThis.location?.href || '',
    ...buildVolunteerEventFields(params),
  });
};

export const trackVolunteerStep = (params: VolunteerTrackingParams) => {
  pushVolunteerEvent(events.registrationStep, params);
};

export const trackVolunteerGuest = (params: {
  guestRelationship: string;
  guestAge: string;
  location: VolunteerLocationInfo;
  isTour?: boolean;
  extraTags?: string[];
}) => {
  trackEvent({
    event: events.registrationGuestAdd,
    ...buildVolunteerGuestContent({
      location: params.location,
      guestRelationship: params.guestRelationship,
      guestAge: params.guestAge,
      isTour: params.isTour,
      extraTags: params.extraTags,
    }),
    content_url: globalThis.location?.href || '',
  });
};

export const trackVolunteerComplete = (params: VolunteerTrackingParams) => {
  pushVolunteerEvent(events.registrationComplete, params);
};
